import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { DviAPI, extractError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const STATUS_PILL = {
    draft: "bg-zinc-100 text-zinc-700",
    in_progress: "bg-sky-100 text-sky-800",
    completed: "bg-indigo-100 text-indigo-800",
    customer_review: "bg-violet-100 text-violet-800",
    customer_approved: "bg-emerald-100 text-emerald-800",
    customer_rejected: "bg-rose-100 text-rose-700",
};

export default function MyInspectionsPage() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("active"); // active | mine | all

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const params = {};
                if (filter === "active") {
                    params.status = "draft,in_progress";
                } else if (filter === "mine") {
                    params.technician_id = "me";
                }
                const r = await DviAPI.listInspections(params);
                setItems(r.items || []);
            } catch (err) {
                toast.error(extractError(err));
            } finally {
                setLoading(false);
            }
        }
        load();
    }, [filter, user?.id]);

    return (
        <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
                        <ClipboardList className="h-6 w-6 text-brand-redDeep" />
                        Inspections
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500">
                        Track every Digital Vehicle Inspection across the workshop.
                    </p>
                </div>
                <div className="flex gap-1 self-start rounded-md border border-zinc-200 bg-white p-1 dark:border-zinc-800 dark:bg-zinc-900">
                    {[
                        { v: "active", label: "Active" },
                        { v: "mine", label: "Mine" },
                        { v: "all", label: "All" },
                    ].map((opt) => (
                        <Button
                            key={opt.v}
                            type="button"
                            size="sm"
                            variant={filter === opt.v ? "default" : "ghost"}
                            onClick={() => setFilter(opt.v)}
                            data-testid={`inspections-filter-${opt.v}`}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex h-32 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                </div>
            ) : items.length === 0 ? (
                <Card className="p-6 text-center text-sm text-zinc-500" data-testid="inspections-empty">
                    No inspections match this filter.
                </Card>
            ) : (
                <div className="space-y-2">
                    {items.map((insp) => {
                        const sum = insp.overall_summary || { green_count: 0, yellow_count: 0, red_count: 0, total_items: 0 };
                        return (
                            <Link
                                key={insp.id}
                                to={`/jobcards/${insp.jobcard_id}/inspection`}
                                data-testid={`inspections-row-${insp.id}`}
                                className="block rounded-lg border border-zinc-200 bg-white p-3 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800/60 sm:p-4"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                                            Job {insp.jobcard_id.slice(-6)}
                                        </div>
                                        <div className="mt-0.5 font-semibold">
                                            Inspection {insp.id.slice(-8)}
                                        </div>
                                        <div className="mt-0.5 text-xs text-zinc-500">
                                            Started {formatDateTime(insp.started_at)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {sum.red_count > 0 && (
                                            <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">
                                                ✕ {sum.red_count}
                                            </span>
                                        )}
                                        {sum.yellow_count > 0 && (
                                            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                                ! {sum.yellow_count}
                                            </span>
                                        )}
                                        {sum.green_count > 0 && (
                                            <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-bold text-white">
                                                ✓ {sum.green_count}
                                            </span>
                                        )}
                                        <span
                                            className={
                                                "rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                                                (STATUS_PILL[insp.status] || STATUS_PILL.draft)
                                            }
                                        >
                                            {(insp.status || "draft").replace(/_/g, " ")}
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
